import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@lognai/synapse-design-system-v2';
import { ChevronLeft, ChevronRight, RefreshCw } from '@lognai/synapse-design-system-v2/icons';
import { buildPurchaseOrderDetailUrl, formatCurrency, listPurchaseOrders } from '@lognai/plugin-orders-ui';
import type { ReportWorkspaceCardContent } from '../../services/api/workspace';
import { useAuth } from '../../hooks/useAuth';

type CalendarView = 'day' | 'week' | 'month';
type Arrival = Awaited<ReturnType<typeof listPurchaseOrders>>['items'][number];
const CLOSED_STATUSES = new Set(['received', 'cancelled']);
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const dateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const dateFor = (key: string): Date => new Date(`${key}T12:00:00`);
const addDays = (date: Date, count: number): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate() + count, 12);
const monday = (date: Date): Date => addDays(date, -((date.getDay() + 6) % 7));
const fullDate = (key: string): string => dateFor(key).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
const arrivalDate = (order: Arrival): string | undefined => {
  const value = order.expectedDeliveryDate as Date | string | undefined;
  return !value ? undefined : typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
};

export function ExpectedArrivalsCard({ onContent }: { onContent?: ReportWorkspaceCardContent }) {
  const navigate = useNavigate();
  const { user, activeCompanyId } = useAuth();
  const today = dateKey(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [view, setView] = useState<CalendarView>('month');
  const anchor = dateFor(selectedDate);
  const start = view === 'month' ? monday(new Date(anchor.getFullYear(), anchor.getMonth(), 1, 12)) : view === 'week' ? monday(anchor) : anchor;
  const end = view === 'month' ? addDays(monday(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 12)), 6) : view === 'week' ? addDays(start, 6) : anchor;
  const from = dateKey(start);
  const to = dateKey(end);
  const days: string[] = [];
  for (let day = start; dateKey(day) <= to; day = addDays(day, 1)) days.push(dateKey(day));

  const arrivals = useQuery({
    // Share the list prefix so PO edits/receipts invalidate the calendar too.
    queryKey: ['purchase-orders', 'list', 'workspace-arrivals', activeCompanyId, user?.id, from, to],
    enabled: !!activeCompanyId,
    queryFn: async ({ signal }) => {
      const orders: Arrival[] = [];
      let page = 1;
      let hasNext = true;
      while (hasNext) {
        signal.throwIfAborted();
        const result = await listPurchaseOrders({
          page, limit: 100,
          filter: { expectedDeliveryDate: { from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z` } },
        });
        orders.push(...result.items);
        hasNext = result.pagination?.hasNext === true;
        page += 1;
      }
      return orders.filter((order) => {
        const date = arrivalDate(order);
        return !CLOSED_STATUSES.has(order.status) && date && date >= from && date <= to;
      });
    },
    staleTime: 30_000,
  });
  useEffect(() => {
    onContent?.('expected-arrivals', {
      state: arrivals.isError ? 'error' : arrivals.isLoading ? 'loading' : !arrivals.data ? 'unknown' : arrivals.data.length ? 'populated' : 'empty',
      calendarView: view, calendarRows: view === 'month' ? days.length / 7 : 1,
      ...(arrivals.data ? { itemCount: arrivals.data.length } : {}),
    });
  }, [arrivals.isError, arrivals.isLoading, arrivals.data, view, days.length, onContent]);
  const byDay = useMemo(() => {
    const groups = new Map<string, Arrival[]>();
    for (const order of arrivals.data ?? []) {
      const key = arrivalDate(order)!;
      const group = groups.get(key);
      if (group) group.push(order);
      else groups.set(key, [order]);
    }
    return groups;
  }, [arrivals.data]);

  const move = (direction: -1 | 1) => {
    const next = view === 'month'
      ? new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1, 12)
      : addDays(anchor, direction * (view === 'week' ? 7 : 1));
    setSelectedDate(dateKey(next));
  };
  const showDay = (day: string) => { setSelectedDate(day); setView('day'); };
  const title = view === 'month'
    ? anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : view === 'day' ? fullDate(selectedDate)
      : `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const renderOrder = (order: Arrival, compact = false) => {
    const date = arrivalDate(order)!;
    const tone = date < today ? 'border-warning/25 bg-warning-subtle text-warning-foreground' : date === today ? 'border-info/25 bg-info-subtle text-info-foreground' : 'border-primary/15 bg-primary/5 text-primary';
    return (
      <button
        key={order.id}
        type="button"
        className={`block w-full min-w-0 rounded border px-2 py-1 text-left transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tone}`}
        onClick={() => navigate(buildPurchaseOrderDetailUrl(order.id))}
        title={`${order.orderNumber} · ${order.supplierName || 'Supplier not specified'} · ${fullDate(date)}`}
      >
        <span className="block truncate text-xs font-semibold">{order.orderNumber}</span>
        <span className="block truncate text-[11px] opacity-80">{order.supplierName || 'Supplier not specified'}</span>
        {!compact && (
          <span className="mt-1 flex flex-wrap items-center justify-between gap-1 text-[11px]">
            <span className="capitalize">{order.status.replace(/_/g, ' ')}{date < today ? ' · Overdue' : date === today ? ' · Due today' : ''}</span>
            <span>{formatCurrency(order.total, order.currency)}</span>
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col" aria-label="Expected arrivals calendar">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 px-0" aria-label={`Previous ${view}`} onClick={() => move(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 px-0" aria-label={`Next ${view}`} onClick={() => move(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedDate(today)}>Today</Button>
          <span className="ml-2 text-sm font-semibold" aria-live="polite">{title}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" aria-label="Arrival date" value={selectedDate} onInput={(event) => { if (/^\d{4}-\d{2}-\d{2}$/.test(event.currentTarget.value)) setSelectedDate(event.currentTarget.value); }} className="h-7 w-[135px] px-2 text-xs" />
          <div role="group" aria-label="Calendar view" className="flex rounded-md border border-border p-0.5">
            {(['day', 'week', 'month'] as const).map((mode) => (
              <Button key={mode} variant={view === mode ? 'secondary' : 'ghost'} size="sm" className="h-6 px-2 text-xs" aria-pressed={view === mode} onClick={() => setView(mode)}>{mode[0].toUpperCase() + mode.slice(1)}</Button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs text-muted-foreground" aria-live="polite">
        <span>{arrivals.isPending ? 'Loading arrivals…' : arrivals.isError ? 'Arrivals unavailable' : `${arrivals.data?.length ?? 0} expected ${(arrivals.data?.length ?? 0) === 1 ? 'arrival' : 'arrivals'}`}</span>
        <span>Open purchase orders · Expected delivery date</span>
      </div>
      {arrivals.isError ? (
        <div role="alert" className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center text-sm text-muted-foreground">
          <p>Expected arrivals could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => void arrivals.refetch()}><RefreshCw className="h-3 w-3" /> Retry</Button>
        </div>
      ) : view === 'day' ? (
        <div className="min-h-0 flex-1 overflow-auto p-3" aria-busy={arrivals.isPending}>
          <div className="space-y-2">{(byDay.get(selectedDate) ?? []).map((order) => renderOrder(order))}</div>
          {!arrivals.isPending && !byDay.has(selectedDate) && <p className="py-8 text-center text-sm text-muted-foreground">No expected arrivals on this day.</p>}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto" aria-busy={arrivals.isPending}>
          <div role="grid" aria-label={`Expected arrivals ${view}`} className="grid min-h-full grid-cols-7" style={{ gridTemplateRows: `24px repeat(${days.length / 7}, minmax(78px, 1fr))` }}>
            {WEEKDAYS.map((day) => <div role="columnheader" key={day} className="sticky top-0 z-10 border-b border-border bg-surface px-1 py-1 text-center text-[10px] font-semibold text-muted-foreground">{day}</div>)}
            {days.map((day) => {
              const orders = byDay.get(day) ?? [];
              const muted = view === 'month' && day.slice(0, 7) !== selectedDate.slice(0, 7);
              const shown = view === 'month' ? orders.slice(0, 2) : orders;
              return (
                <div role="gridcell" key={day} aria-label={`${fullDate(day)}, ${orders.length} arrivals`} className={`min-w-0 border-b border-r border-border p-1.5 ${muted ? 'bg-muted/20' : ''}`}>
                  <button type="button" aria-label={`View arrivals on ${fullDate(day)}`} onClick={() => showDay(day)} className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${day === today ? 'bg-primary font-semibold text-primary-foreground' : muted ? 'text-muted-foreground' : 'text-foreground'}`}>{dateFor(day).getDate()}</button>
                  <div className="space-y-1">{shown.map((order) => renderOrder(order, view === 'month'))}</div>
                  {shown.length < orders.length && <button type="button" className="mt-1 text-[10px] font-medium text-primary hover:underline" onClick={() => showDay(day)}>+{orders.length - shown.length} more</button>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
